import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";

import { cn } from "@lib/utils";

const TooltipProvider = ({ delay = 0, ...props }: TooltipPrimitive.Provider.Props) => (
	<TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />
);

/** A popup that displays information related to an element
 * when the element receives keyboard focus or the mouse hovers over it.
 */
const Tooltip = ({ ...props }: TooltipPrimitive.Root.Props) => <TooltipPrimitive.Root data-slot="tooltip" {...props} />;

/**
 * Wraps the element that triggers the tooltip.
 * By default renders as a `<button>`. To wrap an interactive element (e.g. Button),
 * pass `render={<span className="inline-block" />}` to avoid nested buttons.
 */
const TooltipTrigger = ({ ...props }: TooltipPrimitive.Trigger.Props) => (
	<TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
);

/** TooltipContent is the content of the tooltip.
 * @prop className - The class name to apply to the tooltip content.
 * @prop side - The side of the tooltip content.
 * @prop sideOffset - The offset of the tooltip content.
 * @prop align - The alignment of the tooltip content.
 * @prop alignOffset - The offset of the tooltip content.
 * @prop zIndexMax - Whether to use the maximum z-index.
 * @prop children - The children of the tooltip content.
 */
const TooltipContent = ({
	className,
	side = "top",
	sideOffset = 4,
	align = "center",
	alignOffset = 0,
	zIndexMax = false,
	children,
	...props
}: TooltipPrimitive.Popup.Props &
	Pick<TooltipPrimitive.Positioner.Props, "align" | "alignOffset" | "side" | "sideOffset"> & {
		zIndexMax?: boolean;
	}) => (
	<TooltipPrimitive.Portal>
		<TooltipPrimitive.Positioner
			align={align}
			alignOffset={alignOffset}
			side={side}
			sideOffset={sideOffset}
			className={cn("isolate", zIndexMax ? "z-tooltip" : "z-sticky")}
		>
			<TooltipPrimitive.Popup
				data-slot="tooltip-content"
				className={cn(
					`text-background z-50 w-fit max-w-xs origin-(--transform-origin) rounded-sm bg-secondary px-2 py-1.5 text-xs
					data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in
					data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2
					data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2
					data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2
					data-[side=top]:slide-in-from-bottom-2 data-[state=delayed-open]:animate-in
					data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95`,
					className,
				)}
				{...props}
			>
				{children}
				<TooltipPrimitive.Arrow
					className={`z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-secondary fill-secondary
						data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1
						data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1
						data-[side=inline-start]:-translate-y-1/2 data-[side=left]:top-1/2! data-[side=left]:-right-1
						data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1
						data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5`}
				/>
			</TooltipPrimitive.Popup>
		</TooltipPrimitive.Positioner>
	</TooltipPrimitive.Portal>
);

Tooltip.displayName = "Tooltip";
TooltipTrigger.displayName = "TooltipTrigger";
TooltipContent.displayName = "TooltipContent";
TooltipProvider.displayName = "TooltipProvider";

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
